import React, { useState } from "react";
import {
  Button,
  H2,
  H3,
  PerexBox,
  SystemMessage,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { AdPreview } from "../../elements/AdPreview";
import { ImageAssetLines } from "../../elements/ImageAssetLines";
import { useGenerateMoreImageSuggestions, useImageAssetLines } from "../../elements/ImageAssetLines/useImageAssetLines";
import { SmallLoader } from "../../elements/SmallLoader";
import { TextAssetLines } from "../../elements/TextAssetLines";
import { useGenerateTextSuggestions, useTextAssetLines } from "../../elements/TextAssetLines/useTextAssetLines";
import {
  AssetLineKindT,
  GooglePerformanceLabelT,
  ImageSuggestionKindT,
  SuggestionKindT,
  useActiveImagesAssetLinesQuery,
  useActiveTextsAssetLinesQuery,
  useDiscardDraftAssetLinesMutation,
  useNotDeletedImagesAssetLinesQuery,
  useNotDeletedTextsAssetLinesQuery,
  useOrganizationDataQuery,
  usePublishDraftAssetLinesMutation,
  useRemoveDisapprovedAssetLinesMutation,
} from "../../graphql/generated/graphql";
import { useHasDisapprovedAssets } from "../../hooks/useHasDisapprovedAssets";
import { useHasLowPerformanceAssets } from "../../hooks/useHasLowPerformanceAssets";

const TEXTS = [
  SuggestionKindT.ShortHeadlineT,
  SuggestionKindT.LongHeadlineT,
  SuggestionKindT.ShortDescriptionT,
  SuggestionKindT.LongDescriptionT,
];

const IMAGES = [ImageSuggestionKindT.SquareMarketingImageT, ImageSuggestionKindT.MarketingImageT];

type AssetViewT = "table" | ImageSuggestionKindT | SuggestionKindT;

const GoogleAssets = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Assets Page" });
  const { t: tNav } = useTranslation("translation", { keyPrefix: "Components.AssetsNavigation" });
  const [currentView, setCurrentView] = useState<AssetViewT>("table");
  const [refreshComponent, setRefreshComponent] = useState(1);

  // Table view queries
  const [removeDisapproved, { loading: loadingRemoveDisapproved }] = useRemoveDisapprovedAssetLinesMutation();
  const { data: organizationData, loading: loadingProductsAndDesign } = useOrganizationDataQuery();
  const { data: dataActiveImageAssetLines, loading: loadingImages } = useActiveImagesAssetLinesQuery();
  const { data: dataNotDeletedImageAssetLines } = useNotDeletedImagesAssetLinesQuery();
  const { data: dataActiveTextAssetLines, loading: loadingTexts } = useActiveTextsAssetLinesQuery();
  const { data: dataNotDeletedTextAssetLines } = useNotDeletedTextsAssetLinesQuery();

  // Image asset editor queries
  const { activeImageAssetLines, loadingData: loadingImageData, validateImages } = useImageAssetLines(true);
  const {
    canFetchMore,
    data: imageSuggestionsData,
    generateMoreSuggestions,
    generatingMoreSuggestions,
    loading: loadingImageSuggestions,
  } = useGenerateMoreImageSuggestions();

  // Text asset editor queries
  const { activeTextAssetLines, loadingData: loadingTextData, validateTexts } = useTextAssetLines(true);

  // Text suggestions for each kind (must call at top level)
  const shortHeadlineSuggestions = useGenerateTextSuggestions(SuggestionKindT.ShortHeadlineT);
  const longHeadlineSuggestions = useGenerateTextSuggestions(SuggestionKindT.LongHeadlineT);
  const shortDescriptionSuggestions = useGenerateTextSuggestions(SuggestionKindT.ShortDescriptionT);
  const longDescriptionSuggestions = useGenerateTextSuggestions(SuggestionKindT.LongDescriptionT);

  // Mutations for publish/discard
  const [publishImage, { loading: loadingPublishImage }] = usePublishDraftAssetLinesMutation({
    onCompleted: () => setCurrentView("table"),
  });
  const [discardImage, { loading: loadingDiscardImage }] = useDiscardDraftAssetLinesMutation();
  const [publishText, { loading: loadingPublishText }] = usePublishDraftAssetLinesMutation({
    onCompleted: () => setCurrentView("table"),
  });
  const [discardText, { loading: loadingDiscardText }] = useDiscardDraftAssetLinesMutation();

  const hasAnyDrafts = !!(
    (dataNotDeletedImageAssetLines?.organization?.notDeletedImagesAssetLines || []).find((line) => line.draftState) ||
    (dataNotDeletedTextAssetLines?.organization?.notDeletedTextsAssetLines || []).find((line) => line.draftState)
  );

  const hasAnyDisapproved = useHasDisapprovedAssets();
  const hasAnyLowPerformance = useHasLowPerformanceAssets();

  // Asset navigation
  const renderNavigation = () => (
    <div style={{ display: "flex", gap: "8px", marginBottom: "24px", marginTop: "24px" }}>
      <Button
        data-test-id="assets-table"
        variant={currentView === "table" ? "primary" : "secondary"}
        onClick={() => setCurrentView("table")}
      >
        {tNav("table")}
      </Button>
      <Button
        data-test-id="assets-SQUARE_MARKETING_IMAGE"
        variant={currentView === ImageSuggestionKindT.SquareMarketingImageT ? "primary" : "secondary"}
        onClick={() => setCurrentView(ImageSuggestionKindT.SquareMarketingImageT)}
      >
        {tNav("square_marketing_image")}
      </Button>
      <Button
        data-test-id="assets-MARKETING_IMAGE"
        variant={currentView === ImageSuggestionKindT.MarketingImageT ? "primary" : "secondary"}
        onClick={() => setCurrentView(ImageSuggestionKindT.MarketingImageT)}
      >
        {tNav("marketing_image")}
      </Button>
      <Button
        data-test-id="assets-SHORT_HEADLINE"
        variant={currentView === SuggestionKindT.ShortHeadlineT ? "primary" : "secondary"}
        onClick={() => setCurrentView(SuggestionKindT.ShortHeadlineT)}
      >
        {tNav("short_headline")}
      </Button>
      <Button
        data-test-id="assets-LONG_HEADLINE"
        variant={currentView === SuggestionKindT.LongHeadlineT ? "primary" : "secondary"}
        onClick={() => setCurrentView(SuggestionKindT.LongHeadlineT)}
      >
        {tNav("long_headline")}
      </Button>
      <Button
        data-test-id="assets-SHORT_DESCRIPTION"
        variant={currentView === SuggestionKindT.ShortDescriptionT ? "primary" : "secondary"}
        onClick={() => setCurrentView(SuggestionKindT.ShortDescriptionT)}
      >
        {tNav("short_description")}
      </Button>
      <Button
        data-test-id="assets-LONG_DESCRIPTION"
        variant={currentView === SuggestionKindT.LongDescriptionT ? "primary" : "secondary"}
        onClick={() => setCurrentView(SuggestionKindT.LongDescriptionT)}
      >
        {tNav("long_description")}
      </Button>
    </div>
  );

  // Table view component
  const renderTableView = () => (
    <div>
      {hasAnyDisapproved && (
        <div style={{ marginBottom: "16px" }}>
          <SystemMessage
            level="alert"
            title={t("Disapproved assets alert title")}
            description={
              <Text>
                <Trans
                  components={{ a: <a href="mailto:podpora@shoptet.cz" /> }}
                  i18nKey="Disapproved assets alert description"
                  t={t}
                />
              </Text>
            }
          />
        </div>
      )}
      {hasAnyLowPerformance && (
        <div style={{ marginBottom: "16px" }}>
          <SystemMessage
            level="alert"
            title={t("Low performance assets alert title")}
            description={
              <Text>
                <Trans i18nKey="Low performance assets alert description" t={t} />
              </Text>
            }
          />
        </div>
      )}
      <div style={{ marginBottom: "16px" }}>
        <H3>{t("Assets table title")}</H3>
      </div>
      <div style={{ marginBottom: "16px" }}>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="Assets table description" t={t} />
        </Text>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>{t("table header.asset kind")}</TableHeaderCell>
            <TableHeaderCell>
              <div className="text-center">{t("table header.valid assets")}</div>
            </TableHeaderCell>
            <TableHeaderCell>
              <div className="text-center">{t("table header.rejected assets")}</div>
            </TableHeaderCell>
            {hasAnyLowPerformance && (
              <TableHeaderCell>
                <div className="text-center">{t("table header.low performance assets")}</div>
              </TableHeaderCell>
            )}
            {hasAnyDrafts && (
              <TableHeaderCell>
                <div className="text-center">{t("table header.unpublished changes")}</div>
              </TableHeaderCell>
            )}
            <TableHeaderCell>
              <div style={{ display: "flex", justifyContent: "end" }}>{t("table header.action")}</div>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {IMAGES.map((kind) => {
            const lines = dataActiveImageAssetLines?.organization?.activeImagesAssetLines.filter(
              (line) => (kind as string) === (line.kind as string)
            );
            const disapprovedLines = lines?.filter((line) => line.googleDisapproved);
            const lowPerformanceLines = lines?.filter(
              (line) => line.googlePerformanceLabel === GooglePerformanceLabelT.LowT
            );
            const draftLines =
              dataNotDeletedImageAssetLines?.organization?.notDeletedImagesAssetLines?.filter(
                (line) => (kind as string) === (line.kind as string) && line.draftState
              ) || [];
            return (
              <TableRow key={kind} data-test-id={`table-row-${kind}`}>
                <TableCell>{t(`asset kinds.${kind}`)}</TableCell>
                <TableCell>
                  <div className="text-center" data-test-id="active-count">
                    {loadingImages ? "" : lines?.length}
                  </div>
                </TableCell>
                <TableCell>
                  {loadingImages || disapprovedLines?.length === 0 ? (
                    ""
                  ) : (
                    <div
                      className="text-center"
                      data-test-id="disapproved-count"
                      style={{ color: "var(--app-critical)" }}
                    >
                      {disapprovedLines?.length}
                    </div>
                  )}
                </TableCell>
                {hasAnyLowPerformance && (
                  <TableCell>
                    {loadingImages || lowPerformanceLines?.length === 0 ? (
                      ""
                    ) : (
                      <div
                        className="text-center"
                        data-test-id="low-performance-count"
                        style={{ color: "var(--app-critical)" }}
                      >
                        {lowPerformanceLines?.length}
                      </div>
                    )}
                  </TableCell>
                )}
                {hasAnyDrafts && (
                  <TableCell>
                    <div className="text-center" style={{ color: "var(--app-brand-green)" }}>
                      {draftLines.length > 0 ? draftLines.length : ""}
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <div style={{ display: "flex", justifyContent: "end" }}>
                    {((disapprovedLines && disapprovedLines.length > 0) ||
                      (lowPerformanceLines && lowPerformanceLines.length > 0)) && (
                      <div style={{ marginRight: "10px" }}>
                        <Button
                          data-test-id="replace-bad-assets"
                          disabled={loadingRemoveDisapproved}
                          onClick={() =>
                            removeDisapproved({
                              variables: { kinds: [kind as string as AssetLineKindT], draft: true },
                            }).then(() => setCurrentView(kind))
                          }
                        >
                          {t("replace rejected assets button")}
                        </Button>
                      </div>
                    )}
                    <Button data-test-id="add-new-assets" variant="secondary" onClick={() => setCurrentView(kind)}>
                      {t("add asset button")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {TEXTS.map((kind) => {
            const lines = dataActiveTextAssetLines?.organization?.activeTextsAssetLines.filter(
              (line) => (kind as string) === (line.kind as string)
            );
            const disapprovedLines = lines?.filter((line) => line.googleDisapproved);
            const lowPerformanceLines = lines?.filter(
              (line) => line.googlePerformanceLabel === GooglePerformanceLabelT.LowT
            );
            const draftLines =
              dataNotDeletedTextAssetLines?.organization?.notDeletedTextsAssetLines?.filter(
                (line) => (kind as string) === (line.kind as string) && line.draftState
              ) || [];
            return (
              <TableRow key={kind} data-test-id={`table-row-${kind}`}>
                <TableCell>{t(`asset kinds.${kind}`)}</TableCell>
                <TableCell>
                  <div className="text-center" data-test-id="active-count">
                    {loadingTexts ? "" : lines?.length}
                  </div>
                </TableCell>
                <TableCell>
                  {loadingTexts || disapprovedLines?.length === 0 ? (
                    ""
                  ) : (
                    <div
                      className="text-center"
                      data-test-id="disapproved-count"
                      style={{ color: "var(--app-critical)" }}
                    >
                      {disapprovedLines?.length}
                    </div>
                  )}
                </TableCell>
                {hasAnyLowPerformance && (
                  <TableCell>
                    {loadingTexts || lowPerformanceLines?.length === 0 ? (
                      ""
                    ) : (
                      <div
                        className="text-center"
                        data-test-id="low-performance-count"
                        style={{ color: "var(--app-critical)" }}
                      >
                        {lowPerformanceLines?.length}
                      </div>
                    )}
                  </TableCell>
                )}
                {hasAnyDrafts && (
                  <TableCell>
                    <div className="text-center" style={{ color: "var(--app-brand-green)" }}>
                      {draftLines.length > 0 ? draftLines.length : ""}
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <div style={{ display: "flex", justifyContent: "end" }}>
                    {((disapprovedLines && disapprovedLines.length > 0) ||
                      (lowPerformanceLines && lowPerformanceLines.length > 0)) && (
                      <div style={{ marginRight: "10px" }}>
                        <Button
                          data-test-id="replace-bad-assets"
                          disabled={loadingRemoveDisapproved}
                          onClick={() =>
                            removeDisapproved({
                              variables: { kinds: [kind as string as AssetLineKindT], draft: true },
                            }).then(() => setCurrentView(kind))
                          }
                        >
                          {t("replace rejected assets button")}
                        </Button>
                      </div>
                    )}
                    <Button data-test-id="add-new-assets" variant="secondary" onClick={() => setCurrentView(kind)}>
                      {t("add asset button")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div
        style={{
          background: "var(--app-surface-muted)",
          borderRadius: "var(--app-radius-box)",
          padding: "10px",
          margin: "20px 0",
        }}
      >
        <H2>{t("Preview Headline")}</H2>
        {loadingImages || loadingTexts || loadingProductsAndDesign ? (
          <SmallLoader />
        ) : (
          <AdPreview
            activeImagesAssetLines={dataActiveImageAssetLines?.organization?.activeImagesAssetLines || []}
            activeTextAssetLines={dataActiveTextAssetLines?.organization?.activeTextsAssetLines || []}
            organization={organizationData?.organization}
          />
        )}
      </div>
    </div>
  );

  // Image asset editor
  const renderImageAssetEditor = (kind: ImageSuggestionKindT) => {
    const errors = (validateImages?.errors || []).filter((error) => (error.attribute as string) === (kind as string));
    const hasAnyDraft = !!activeImageAssetLines?.find(
      (line) => (line.kind as string) === (kind as string) && !!line.draftState
    );

    return (
      <div>
        <PerexBox>{t("InfoBox-Do not update too often")}</PerexBox>
        <div style={{ margin: "24px 0" }}>
          <H2>{t(`asset kinds.${kind}`)}</H2>
        </div>
        {loadingImageSuggestions || loadingImageData ? (
          <SmallLoader />
        ) : (
          <>
            <ImageAssetLines
              activeImagesAssetLines={activeImageAssetLines || []}
              canFetchMore={canFetchMore}
              errors={validateImages?.errors || []}
              generateMoreSuggestions={generateMoreSuggestions}
              generatingMoreSuggestions={generatingMoreSuggestions}
              imageSuggestions={imageSuggestionsData?.organization?.imageSuggestions || []}
              kind={kind}
              limit={5}
              draft
              hideSubtext
            />
            {hasAnyDraft ? (
              <>
                <div style={{ display: "inline-block", marginRight: "10px" }}>
                  <Button
                    disabled={loadingPublishImage || errors.length > 0}
                    onClick={() => publishImage({ variables: { kinds: [kind as string as AssetLineKindT] } })}
                  >
                    {loadingPublishImage ? t("publishing") : t("publish changes")}
                  </Button>
                </div>
                <Button
                  disabled={loadingDiscardImage}
                  variant="secondary"
                  onClick={() => discardImage({ variables: { kinds: [kind as string as AssetLineKindT] } })}
                >
                  {loadingDiscardImage ? t("reverting changes") : t("revert changes")}
                </Button>
              </>
            ) : (
              <Button disabled>{t("publish changes")}</Button>
            )}
          </>
        )}
      </div>
    );
  };

  // Text asset editor
  const renderTextAssetEditor = (kind: SuggestionKindT) => {
    // Select the right hook data based on kind
    const suggestionsDataMap: Record<
      | SuggestionKindT.ShortHeadlineT
      | SuggestionKindT.LongHeadlineT
      | SuggestionKindT.ShortDescriptionT
      | SuggestionKindT.LongDescriptionT,
      ReturnType<typeof useGenerateTextSuggestions>
    > = {
      [SuggestionKindT.ShortHeadlineT]: shortHeadlineSuggestions,
      [SuggestionKindT.LongHeadlineT]: longHeadlineSuggestions,
      [SuggestionKindT.ShortDescriptionT]: shortDescriptionSuggestions,
      [SuggestionKindT.LongDescriptionT]: longDescriptionSuggestions,
    };

    const suggestionsData = suggestionsDataMap[kind as keyof typeof suggestionsDataMap];

    const {
      data: textSuggestionsData,
      generateMoreSuggestions: generateMoreTextSuggestions,
      generatingMoreSuggestions: generatingMoreTextSuggestions,
      loading: loadingTextSuggestions,
    } = suggestionsData;

    const errors = (validateTexts?.errors || []).filter((error) => (error.attribute as string) === (kind as string));
    const hasAnyDraft = !!activeTextAssetLines?.find(
      (line) => (line.kind as string) === (kind as string) && line.draftState
    );

    return (
      <div>
        <PerexBox>{t("InfoBox-Do not update too often")}</PerexBox>
        {loadingTextSuggestions || loadingTextData ? (
          <SmallLoader />
        ) : (
          <>
            <TextAssetLines
              key={`${kind}-${refreshComponent}`}
              activeTextAssetLines={activeTextAssetLines || []}
              errors={validateTexts?.errors || []}
              generateMoreSuggestions={generateMoreTextSuggestions}
              generatingMoreSuggestions={generatingMoreTextSuggestions}
              kind={kind}
              textSuggestions={textSuggestionsData?.organization?.textSuggestions || []}
              draft
              hideSubtext
            />
            <br />
            {hasAnyDraft ? (
              <>
                <div style={{ display: "inline-block", marginRight: "10px" }}>
                  <Button
                    disabled={loadingPublishText || errors.length > 0}
                    onClick={() => publishText({ variables: { kinds: [kind as string as AssetLineKindT] } })}
                  >
                    {loadingPublishText ? t("publishing") : t("publish changes")}
                  </Button>
                </div>
                <Button
                  disabled={loadingDiscardText}
                  variant="secondary"
                  onClick={() =>
                    discardText({ variables: { kinds: [kind as string as AssetLineKindT] } }).then(() =>
                      setRefreshComponent((state) => state + 1)
                    )
                  }
                >
                  {loadingDiscardText ? t("reverting changes") : t("revert changes")}
                </Button>
              </>
            ) : (
              <Button disabled>{t("publish changes")}</Button>
            )}
          </>
        )}
      </div>
    );
  };

  // Main content renderer
  const renderContent = () => {
    if (currentView === "table") {
      return renderTableView();
    } else if (IMAGES.includes(currentView as ImageSuggestionKindT)) {
      return renderImageAssetEditor(currentView as ImageSuggestionKindT);
    } else if (TEXTS.includes(currentView as SuggestionKindT)) {
      return renderTextAssetEditor(currentView as SuggestionKindT);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <H2>{t("Google assets")}</H2>
      </div>
      {renderNavigation()}
      {renderContent()}
    </div>
  );
};

export default GoogleAssets;
